import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiPlus, 
  FiFilter, 
} from 'react-icons/fi';
import SnagCard from './components/SnagCard';
import SnagFilters from './components/SnagFilters';
import CreateSnagModal from './components/CreateSnagModal';
import { Snag, SnagFilters as FiltersType, BoardColumn } from '../../shared/types/snag';
import { useToast } from '../../components/toast/toastProvider';
import SnagTrackerService from '../../services/snagTrackerService';
import './SnagBoard.css';
import ExportMenu from './components/ExportMenu';

const BOARD_COLUMNS: BoardColumn[] = [
  { id: 'new', title: 'New', status: 'New', color: '#ef4444' },
  { id: 'assigned', title: 'Assigned', status: 'Assigned', color: '#f59e0b' },
  { id: 'in-progress', title: 'In Progress', status: 'In Progress', color: '#3b82f6' },
  { id: 'under-review', title: 'Under Review', status: 'Under Review', color: '#8b5cf6' },
  { id: 'resolved', title: 'Resolved', status: 'Resolved', color: '#10b981' },
  { id: 'closed', title: 'Closed', status: 'Closed', color: '#6b7280' }
];

const SnagBoard: React.FC = () => {
  const [snags, setSnags] = useState<Snag[]>([]);
  const [filteredSnags, setFilteredSnags] = useState<Snag[]>([]);
  const [filters, setFilters] = useState<FiltersType>({
    search: '',
    priority: [],
    assignedTo: [],
    location: [],
    tags: [],
    dateRange: {}
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [draggedSnag, setDraggedSnag] = useState<Snag | null>(null);
  
  const { addToast } = useToast();

  // Load snags from API
  const loadSnags = useCallback(async () => {
    try {
      const response = await SnagTrackerService.getSnags();
      const snagList = response.snags || [];
      setSnags(snagList);
      setFilteredSnags(snagList);
    } catch (error) {
      console.error('Failed to load snags:', error);
      addToast({
        type: 'error',
        title: 'Error Loading Snags',
        message: 'Failed to load snags from server',
        duration: 5000
      });
    }
  }, [addToast]);

  // Load snags from API on mount
  useEffect(() => {
    loadSnags();
  }, [loadSnags]);

  // Handle drag and drop
  const handleDragStart = (snag: Snag) => {
    setDraggedSnag(snag);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, newStatus: Snag['status']) => {
    e.preventDefault();
    if (draggedSnag && draggedSnag.status !== newStatus) {
      try {
        // Update backend
        await SnagTrackerService.updateSnagStatus(draggedSnag.id, newStatus);
        
        // Update local state
        const updatedSnags = snags.map(snag =>
          snag.id === draggedSnag.id ? { ...snag, status: newStatus } : snag
        );
        setSnags(updatedSnags);
        setFilteredSnags(updatedSnags);
        
        addToast({
          type: 'success',
          title: 'Snag Updated',
          message: `"${draggedSnag.title}" moved to ${newStatus}`,
          duration: 3000
        });
      } catch (error) {
        console.error('Failed to update snag status:', error);
        addToast({
          type: 'error',
          title: 'Update Failed',
          message: 'Failed to update snag status',
          duration: 5000
        });
      }
    }
    setDraggedSnag(null);
  };

  // Filter snags
  useEffect(() => {
    let filtered = snags;

    if (filters.search) {
      filtered = filtered.filter(snag =>
        snag.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        snag.description.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.priority.length > 0) {
      filtered = filtered.filter(snag => filters.priority.includes(snag.priority));
    }

    if (filters.assignedTo.length > 0) {
      filtered = filtered.filter(snag => filters.assignedTo.includes(snag.assignedTo.id));
    }

    setFilteredSnags(filtered);
  }, [filters, snags]);

  const getSnagsByStatus = (status: Snag['status']) => {
    return filteredSnags.filter(snag => snag.status === status);
  };

  const getTotalSnagsByPriority = (priority: string) => {
    return filteredSnags.filter(snag => snag.priority === priority).length;
  };

  return (
    <div className="snag-board">
      {/* Header */}
      <div className="snag-board-header">
        <div className="header-left">
          <h1 className="board-title">Timeline</h1>
          <div className="board-stats">
            <div className="stat-item critical">
              <span className="stat-number">{getTotalSnagsByPriority('Critical')}</span>
              <span className="stat-label">Critical</span>
            </div>
            <div className="stat-item high">
              <span className="stat-number">{getTotalSnagsByPriority('High')}</span>
              <span className="stat-label">High</span>
            </div>
            <div className="stat-item medium">
              <span className="stat-number">{getTotalSnagsByPriority('Medium')}</span>
              <span className="stat-label">Medium</span>
            </div>
            <div className="stat-item low">
              <span className="stat-number">{getTotalSnagsByPriority('Low')}</span>
              <span className="stat-label">Low</span>
            </div>
          </div>
        </div>

        <div className="header-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search snags..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          
          <button
            className={`filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter />
            Filters
          </button>

        <ExportMenu
        allSnags={snags}
        filteredSnags={filteredSnags}
        hasActiveFilters={
          filters.search !== '' ||
          filters.priority.length > 0 ||
          filters.assignedTo.length > 0 ||
          filters.location.length > 0 ||
          filters.tags.length > 0 ||
          !!filters.dateRange.start ||
          !!filters.dateRange.end
        }
      />

          <button
            className="action-btn primary"
            onClick={() => setShowCreateModal(true)}
          >
            <FiPlus />
            Snag
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <SnagFilters
          filters={filters}
          onFiltersChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Board Columns */}
      <div className="board-container">
        {BOARD_COLUMNS.map(column => {
          const columnSnags = getSnagsByStatus(column.status);
          
          return (
            <div
              key={column.id}
              className="board-column"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.status)}
            >
              <div className="column-header" style={{ borderTopColor: column.color }}>
                <h3 className="column-title">
                  {column.title}
                  <span className="snag-count">{columnSnags.length}</span>
                </h3>
                {column.limit && columnSnags.length >= column.limit && (
                  <span className="limit-warning">Limit reached!</span>
                )}
              </div>

              <div className="column-content">
                {columnSnags.map(snag => (
                  <SnagCard
                    key={snag.id}
                    snag={snag}
                    onDragStart={() => handleDragStart(snag)}
                  />
                ))}
                
                {columnSnags.length === 0 && (
                  <div className="empty-column">
                    <p>No snags in this state</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Snag Modal */}
      {showCreateModal && (
        <CreateSnagModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(newSnag) => {
            setSnags([...snags, newSnag]);
            setFilteredSnags([...filteredSnags, newSnag]);
            setShowCreateModal(false);
            addToast({
              type: 'success',
              title: 'Snag Created',
              message: `"${newSnag.title}" has been created successfully`,
              duration: 4000
            });
            // Reload snags to ensure consistency
            loadSnags();
          }}
        />
      )}
    </div>
  );
};

export default SnagBoard;
